import { useContext, useEffect, useState } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'

import { AuthContext } from '../context/AuthContext'
import { getSessionsByUserId } from '../service/sessionService'
import Navbar from '../component/workspace/components/Navbar'
import { conditionTagOf, isExperimentSession } from '../component/session/experiment/experimentConfig'

// Deliberately not the InstructorDashboardPage pattern (5 nested providers fetching
// class-wide analytics/survey data for every student) - this is one student looking at
// their own past exercises, so it only needs the session list itself.
const StudentDashboardPage = () => {
  const { currentUser, currentUserLoading } = useContext(AuthContext)
  const navigate = useNavigate()
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!currentUser?.id) return
    getSessionsByUserId(currentUser.id)
      .then((data) => {
        // The user_session join table can carry more than one row per (user, session) pair
        // - useSessionJoin.js calls joinSession() on every mount where session_id already
        // matches, not just the first join, so a student who signs back into the same
        // exercise gets a second row. Dedupe here rather than in the join path, which is a
        // separate, pre-existing behavior this pass isn't otherwise touching.
        // An experiment's own row is not an exercise (its students work in its conditions), so
        // a student never sees it - nor that there is one.
        const seen = new Set()
        const unique = (data || []).filter((s) => {
          if (seen.has(s.id) || isExperimentSession(s)) return false
          seen.add(s.id)
          return true
        })
        setSessions(unique)
      })
      .finally(() => setLoading(false))
  }, [currentUser?.id])

  if (currentUserLoading) return <div className="home"><Navbar sessionId={null} /></div>
  if (!currentUser) return <Navigate to="/login" replace />
  // Guests (role 3) have no way to log back in to see this page again, so this view is
  // scoped to real student accounts only (role 4 - role 2 is TA, see role<=2 permission
  // checks throughout the app).
  if (currentUser.role !== 4) return <Navigate to="/" replace />

  const formatDate = (value) => {
    if (!value) return ''
    return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  }

  return (
    <div className="home">
      <Navbar sessionId={null} />
      <div className="container" style={{ maxWidth: '720px', margin: '0 auto', padding: '32px 20px' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '4px' }}>My Exercises</h1>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>Past exercises you've participated in. You can revisit them, but not edit them.</p>

        {loading ? (
          <div style={{ color: '#6b7280' }}>Loading...</div>
        ) : sessions.length === 0 ? (
          <div style={{ color: '#6b7280' }}>You haven't joined any exercises yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {sessions.map((session) => (
              <button
                key={session.id}
                onClick={() => navigate(`/my-exercises/${session.id}`)}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  textAlign: 'left', padding: '14px 16px', borderRadius: '10px',
                  border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{session.subject || session.task || `Session ${session.id}`}</div>
                  {session.task && session.subject && (
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '2px' }}>
                      {session.task.length > 90 ? `${session.task.slice(0, 90)}...` : session.task}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                  {/* An experiment condition shows its name alone: its type says nothing a
                      student needs, and nothing about the experiment either. */}
                  {session.type && !conditionTagOf(session) && (
                    <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#374151', background: '#f3f4f6', padding: '3px 8px', borderRadius: '99px' }}>
                      {session.type}
                    </span>
                  )}
                  <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>{formatDate(session.created_time)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default StudentDashboardPage
